// =============================================================================
// src/views/negocios/negocios-controller.ts — Comportamento da Tela 06 (Negocios).
//
// Proveniencia: assets/unified-app.js Sections.negocios (linhas 1505-1586). Porte
//   FIEL de activate/_applyMode/_updateInsight/deactivate. Estado vivo: currentTab
//   ('sectors') + currentMode ('clusters'). Cores/opacidades por zona de cada modo
//   (clusters/heatmap/dominant) copiadas verbatim — paridade visual.
//
// Reset de estilo: o legado faz `MapController.currentZoneStyleFn = null` no
//   deactivate (campo privado no MapController TS, fora do contrato). Usamos a mesma
//   extensao local ja adotada por src/views/mapa-eleitoral/index.ts.
//
// SRP: estado + listeners + estilos de mapa da secao. Markup no index.ts.
// =============================================================================

import type { PathOptions } from 'leaflet';
import type { MapController, ZonaKey, HexColor } from '../../app/types';
import { qs, qsa } from '../../app/dom';
import { bindOnce, setActiveItem, syncInlineTabs } from '../../core/ui';
import { MANAUS_CNPJ_MARKERS, pickColorFromRamp } from '../../data';

/** Modos de visualizacao do mapa economico (data-mode da mode-bar). */
type NegociosMode = 'clusters' | 'heatmap' | 'dominant';

/** Campo privado de estilo zerado no deactivate (fora do contrato publico). */
interface MapControllerZoneStyleState {
  currentZoneStyleFn: unknown;
}

/** Estilo de path comum aos modos (mesma borda/peso do legado, opacidade variavel). */
function zonePathStyle(fillColor: HexColor, fillOpacity: number): PathOptions {
  return { fillColor, fillOpacity, weight: 0.8, color: 'rgba(255,255,255,0.5)', opacity: 1 };
}

/** Opacidade-base por zona no modo clusters (vals * 0.62 no legado, 1552-1554). */
const CLUSTER_OPACITY_BY_ZONE: Record<ZonaKey, number> = {
  norte: 0.55, leste: 0.80, 'centro-oeste': 0.65, 'centro-sul': 0.92, oeste: 0.40, sul: 0.72,
};

/** Intensidade por zona no modo heatmap (0..1 -> rampa, 1558-1560). */
const HEATMAP_INTENSITY_BY_ZONE: Record<ZonaKey, number> = {
  norte: 0.58, leste: 0.92, 'centro-oeste': 0.68, 'centro-sul': 0.78, oeste: 0.35, sul: 0.62,
};

/** Rampa ambar do heatmap economico (1560). */
const HEATMAP_RAMP: readonly HexColor[] = ['#FAEEDA', '#FAC775', '#EF9F27', '#BA7517', '#854F0B'];

/** Cor do setor dominante por zona no modo dominant (1563). */
const DOMINANT_FILL_BY_ZONE: Record<ZonaKey, HexColor> = {
  norte: '#534AB7', leste: '#BA7517', 'centro-oeste': '#185FA5', 'centro-sul': '#5F5E5A', oeste: '#639922', sul: '#185FA5',
};

/** Cor de fallback de zona sem mapeamento (`|| '#888780'`). */
const NEGOCIOS_FALLBACK_FILL: HexColor = '#888780';

/** Texto do insight por modo (_updateInsight.text, 1574-1578). */
const NEGOCIOS_INSIGHT_BASE: Record<NegociosMode, string> = {
  clusters: 'Clusters ativos mostram concentração empresarial no eixo Leste/Centro-Sul. Setores selecionados: ',
  heatmap: 'Heatmap econômico destaca Leste como zona de maior densidade de CNPJs e Centro-Sul como polo de receita alta por empresa.',
  dominant: 'Setor dominante por zona expõe territórios com narrativa econômica distinta: indústria no Norte, comércio no Leste e serviços no Sul.',
};

/**
 * Controlador da secao Negocios. Aplica o estilo do modo corrente, troca de abas
 * (setores/grupos/doacoes), filtra setores e narra o insight. Espelha o objeto
 * literal Sections.negocios com estado privado e metodos curtos.
 */
class NegociosSectorController {
  private currentTab = 'sectors';
  private currentMode: NegociosMode = 'clusters';

  /** Ativa a secao (replica activate, 1510-1543): estilo, abas, modos, chips, insight. */
  activate(mapCtrl: MapController): void {
    this.applyMode(mapCtrl);
    this.bindContentTabs();
    syncInlineTabs(qsa('.u-neg-tab'), qs('.u-neg-tab.active'));
    this.bindModeTabs(mapCtrl);
    this.bindSectorChips();
    this.updateInsight();
  }

  /** Aplica o estilo de zona do modo corrente e (re)injeta marcadores no modo clusters. */
  private applyMode(mapCtrl: MapController): void {
    mapCtrl.removeMarkerLayer('negocios');
    if (this.currentMode === 'clusters') return this.applyClustersMode(mapCtrl);
    if (this.currentMode === 'heatmap') return this.applyHeatmapMode(mapCtrl);
    this.applyDominantMode(mapCtrl);
  }

  /** Modo clusters: ambar por opacidade de zona + marcadores de CNPJ (1550-1556). */
  private applyClustersMode(mapCtrl: MapController): void {
    mapCtrl.setZoneStyles((zonaKey) =>
      zonePathStyle('#EF9F27', (CLUSTER_OPACITY_BY_ZONE[zonaKey] ?? 0.5) * 0.62),
    );
    mapCtrl.addMarkerLayer('negocios', MANAUS_CNPJ_MARKERS);
  }

  /** Modo heatmap: cor da rampa ambar por intensidade de zona (1557-1561). */
  private applyHeatmapMode(mapCtrl: MapController): void {
    mapCtrl.setZoneStyles((zonaKey) =>
      zonePathStyle(
        pickColorFromRamp(HEATMAP_INTENSITY_BY_ZONE[zonaKey] ?? 0.5, 0.35, 0.92, HEATMAP_RAMP),
        0.78,
      ),
    );
  }

  /** Modo dominant: cor fixa do setor dominante por zona (1562-1566). */
  private applyDominantMode(mapCtrl: MapController): void {
    mapCtrl.setZoneStyles((zonaKey) =>
      zonePathStyle(DOMINANT_FILL_BY_ZONE[zonaKey] ?? NEGOCIOS_FALLBACK_FILL, 0.72),
    );
  }

  /** Liga as abas de conteudo (.u-neg-tab): sublinhado + troca de painel (1515-1524). */
  private bindContentTabs(): void {
    qsa('.u-neg-tab').forEach((tab) => {
      bindOnce(tab as HTMLElement, 'click', 'negocios-tab', () => this.onContentTab(tab));
    });
  }

  /** Corpo do clique numa aba de conteudo (1516-1523). */
  private onContentTab(tab: Element): void {
    syncInlineTabs(qsa('.u-neg-tab'), tab);
    this.currentTab = (tab as HTMLElement).dataset['tab'] ?? this.currentTab;
    qsa('.u-neg-panel').forEach((panel) => {
      const matches = (panel as HTMLElement).dataset['panel'] === this.currentTab;
      (panel as HTMLElement).style.display = matches ? 'block' : 'none';
      panel.classList.toggle('active', matches);
    });
  }

  /** Liga as mode-tabs (.u-mode-tab da secao): troca de modo + reaplica + insight (1527-1534). */
  private bindModeTabs(mapCtrl: MapController): void {
    qsa('#section-negocios .u-mode-tab').forEach((tab) => {
      bindOnce(tab as HTMLElement, 'click', 'negocios-mode', () => this.onModeTab(mapCtrl, tab));
    });
  }

  /** Corpo do clique numa mode-tab (1528-1533). */
  private onModeTab(mapCtrl: MapController, tab: Element): void {
    setActiveItem(qsa('#section-negocios .u-mode-tab'), tab);
    this.currentMode = readMode(tab);
    this.applyMode(mapCtrl);
    this.updateInsight();
  }

  /** Liga os chips de setor (.u-sector-chip): alterna selecao + recalcula insight (1537-1542). */
  private bindSectorChips(): void {
    qsa('.u-sector-chip').forEach((chip) => {
      bindOnce(chip as HTMLElement, 'click', 'negocios-sector', () => {
        chip.classList.toggle('selected');
        this.updateInsight();
      });
    });
  }

  /**
   * Atualiza o texto do insight (#u-neg-insight-text). O elemento nao existe no
   * markup atual (nem no legado), entao o guard `if (!el) return` torna isto um
   * no-op fiel — preservamos a logica para paridade byte-a-byte (1570-1579).
   */
  private updateInsight(): void {
    const el = qs('#u-neg-insight-text');
    if (!el) return;
    el.textContent = this.insightText();
  }

  /** Monta o texto do insight do modo corrente, listando os setores selecionados. */
  private insightText(): string {
    if (this.currentMode !== 'clusters') return NEGOCIOS_INSIGHT_BASE[this.currentMode];
    const selected = qsa('#section-negocios .u-sector-chip.selected')
      /* v8 ignore next -- textContent de Element nunca é null; ?? '' defensivo CLAUDE.md rule 11 */
      .map((chip) => (chip.textContent ?? '').trim())
      .filter((label) => label !== 'Todos');
    return `${NEGOCIOS_INSIGHT_BASE.clusters}${selected.join(', ') || 'nenhum setor específico'}.`;
  }

  /** Desativa a secao (replica deactivate, 1582-1585): remove marcadores e zera estilo. */
  deactivate(mapCtrl: MapController): void {
    mapCtrl.removeMarkerLayer('negocios');
    (mapCtrl as MapController & MapControllerZoneStyleState).currentZoneStyleFn = null;
  }
}

/** Le e valida o modo de uma mode-tab (data-mode), com fallback 'clusters' (1530). */
function readMode(tab: Element): NegociosMode {
  const raw = (tab as HTMLElement).dataset['mode'];
  return raw === 'heatmap' || raw === 'dominant' ? raw : 'clusters';
}

/** Instancia unica do controlador (espelha o singleton Sections.negocios). */
export const negociosController = new NegociosSectorController();
